<template>
  <div class="elements-foreground-list">
    <button-item
      v-for="elementNode of elementNodes"
      :key="elementNode.uid"
      :selected="selectedNodes.some((node) => node.uid === elementNode.uid)"
      :disabled="disabledUids.has(elementNode.uid)"
      class="elements-foreground-list__button"
      @contextmenu.stop.prevent="onContextMenu(elementNode, $event)"
    >
      <element-list-line
        :element-uid="(elementNode.node.data.elementUid as Uid<'resource' | 'target'>)"
        :impulse="uidImpulses[elementNode.node.data.elementUid]"
        :open-effects="uidEffects[elementNode.node.data.elementUid] ?? []"
        :cost="costs[elementNode.node.data.elementUid]"
        :value="Number(elementValues?.[elementNode.node.data.elementUid]?.value ?? 0)"
        :selection="selection"
        @click="onSelect(elementNode)"
      />
    </button-item>
  </div>
</template>

<script setup lang="ts">
import ElementListLine from "./ElementListLine.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

import type { ImpulseItem } from "../impulse/ImpulseItem";
import type { BaseEffectItem } from "../effect/EffectItem";
import type { ExpressionValue } from "../expression/expressionFunctions";
import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { SceneTreeNode } from "../scene/SceneTreeItem";
import { toUid, type Uid } from "@/helpers/utility/Id";
import { computed } from "vue";

interface Props {
  elementNodes: SceneTreeNode<"element">[];
  disabledUids?: Set<Uid<string>>;
  selectedNode?: SceneTreeNode<"element">;
  selectedNodes?: SceneTreeNode[];
  impulses?: { target: { [elementId: number]: ImpulseItem; }; resource: { [elementId: number]: ImpulseItem }; index?: { [elementId: number]: undefined } };
  openEffects?: { target: { [elementId: number]: BaseEffectItem[] }; resource: { [elementId: number]: BaseEffectItem[] }; index?: { [elementId: number]: undefined } };
  costs?: { [elementUid: string]: { value: number; actionId: number }[] };
  elementValues?: { [uid: string]: ExpressionValue };
  selection?: CanvasSelection;
}
const props = withDefaults(defineProps<Props>(), {
  selectedNode: undefined,
  selectedNodes: () => [],
  impulses: () => ({ target: {}, resource: {}, index: {} }),
  openEffects: () => ({ target: {}, resource: {}, index: {} }),
  costs: () => ({}),
  elementValues: () => ({}),
  disabledUids: () => new Set<Uid<string>>()
});

const emit = defineEmits<{
  (e: "select", item: SceneTreeNode<"element">): void;
  (e: "contextmenu", event: MouseEvent & { items?: { uid: Uid<"sceneNode"> }[] }): void;
}>();

const uidImpulses = computed(() => {
  const result: { [elementUid: string]: ImpulseItem | undefined } = {};
  for (const type of ["target", "resource", "index"] as const) {
    for (const [elementId, impulse] of Object.entries(props.impulses?.[type] ?? {})) {
      const uid = toUid(type, Number(elementId));
      result[uid] = impulse;
    }
  }
  return result;
});
const uidEffects = computed(() => {
  const result: { [elementUid: string]: BaseEffectItem[] | undefined } = {};
  for (const type of ["target", "resource", "index"] as const) {
    for (const [elementId, effects] of Object.entries(props.openEffects?.[type] ?? {})) {
      const uid = toUid(type, Number(elementId));
      result[uid] = effects;
    }
  }
  return result;
});

function onSelect(elementNode: SceneTreeNode<"element">) {
  emit("select", elementNode);
}

function onContextMenu(elementNode: SceneTreeNode<"element">, event: MouseEvent & { items?: { uid: Uid<"sceneNode"> }[] }) {
  event.items = [{ uid: elementNode.uid }];
  emit("contextmenu", event);
}
</script>

<style lang="scss">
.elements-foreground-list {
  min-height: 2.5rem;
  padding-inline: 0.75rem;
  padding-block: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(7rem, 20rem));
  position: relative;
  gap: 0.75rem;

  &__button {
    pointer-events: auto;
  }
}
</style>